import React, { useState, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';

import { Box, Button, Drawer, Title } from '@mantine/core';
import { useTranslation } from 'i18n';
import { PhotoBannerAlert } from 'components/MenuManagement/MenuEdit/MenuContent/PhotoBannerAlert';
import { useMediaQuery } from '@mantine/hooks';
import {
  MENU_OBJECT_TYPES,
  CUBOH_MENU_CHILD_MAP,
  TYPE_TO_READABLE_STRING_MAP,
} from 'constants/menuManagement';
import { useMenuManagement } from 'hooks/useMenuManagement';

import { breakpoints } from 'styles/theme.variables';

import DockedDrawerFormContent from 'primitives/DockedDrawer/DockedDrawerFormContent';
import { AddNewDrawerContextProvider } from 'context/AddNewDrawerContextProvider';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';
import {
  useHandleErrorNotification,
  useHandleSuccessNotification,
} from 'components/MenuManagement/MenuEdit/hooks/useMMNotifications';

import { AddNewDrawerField } from './AddNewDrawerField';
import AddNewDrawerFieldRow from './AddNewDrawerFieldRow';
import { useAddNewDrawerForm } from './hooks/useAddNewDrawerForm';
import { useAddNewDrawerColumns } from './hooks/useAddNewDrawerColumns';
import { formSubmitHandler } from './helpers/formSubmitHandler';

const NS = 'menuManagementAddNewDrawer';

const hasNoImageUrl = type => [MENU_OBJECT_TYPES.modifierGroups].includes(type);

export default function AddNewDrawer({
  itemType,
  parentRecord,
  opened,
  close,
  openAddTypeChoiceDrawer,
  fromTypeChoice = false,
}) {
  const { t } = useTranslation(NS);
  const [newItemPrice, setNewItemPrice] = useState(null);
  const { createNewCubohItem } = useMenuManagement();
  const [uploadImageFile, setUploadImageFile] = useState(null);
  const [defaultModifierChecked, setDefaultModifierChecked] = useState(false);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleErrorNotification = useHandleErrorNotification();
  const handleSuccessNotification = useHandleSuccessNotification();

  const { isCateringMenu, menu, refetchPhotos } = useContext(MenuEdit.Context);

  const newItemDefaults = useMemo(
    () => ({
      parentUuid: parentRecord?.uuid,
      companyId: parentRecord?.companyId != null ? Number(parentRecord.companyId) : undefined,
      merchantId: parentRecord?.merchantId != null ? Number(parentRecord.merchantId) : undefined,
      itemType: CUBOH_MENU_CHILD_MAP[parentRecord?.itemType],
      enabled: true,
      ...(!hasNoImageUrl(CUBOH_MENU_CHILD_MAP[parentRecord?.itemType]) && {
        imageUrl: '',
      }),
    }),
    [parentRecord],
  );

  const { form, newItemHasClassifications, pricesRequired } = useAddNewDrawerForm({
    itemType,
    parentRecord,
    newItemDefaults,
    isCateringMenu,
  });

  const { newItemColumns } = useAddNewDrawerColumns({ newItemDefaults, isCateringMenu });

  const onSubmit = formSubmitHandler({
    close,
    createNewCubohItem,
    defaultModifierChecked,
    menuId: menu?.uuid,
    newItemDefaults,
    newItemHasClassifications,
    newItemPrice,
    pricesRequired,
    resetFields: form.reset,
    uploadImageFile,
    setIsSubmitting,
    setIsUploading,
    refetchPhotos,
    handleErrorNotification,
    handleSuccessNotification,
  });

  const handleSubmit = () => {
    if (isSubmitting) return;
    const validation = form.validate();
    if (!validation.hasErrors) {
      onSubmit(form.values);
    }
  };

  const canHavePhoto = newItemDefaults.itemType === MENU_OBJECT_TYPES.items;

  const renderField = column => (
    <AddNewDrawerContextProvider
      newItemDefaults={newItemDefaults}
      parentRecord={parentRecord}
      setNewItemPrice={setNewItemPrice}
      setUploadImageFile={setUploadImageFile}
      setDefaultModifierChecked={setDefaultModifierChecked}
      setIndividualMinimum={value => form.setFieldValue('individualMinimum', value)}
      setIndividualMaximum={value => form.setFieldValue('individualMaximum', value)}
      isUploading={isUploading}
      opened={opened}
    >
      <AddNewDrawerField column={column} form={form} />
    </AddNewDrawerContextProvider>
  );

  return (
    <Drawer
      opened={opened}
      onClose={() => {
        form.reset();
        close();
      }}
      size={isMobile ? 'sm' : 'lg'}
      title={
        <Title order={6}>
          {t('AddNewDrawer.title', { itemType: TYPE_TO_READABLE_STRING_MAP[itemType] })}
        </Title>
      }
      variant="withDock"
    >
      <DockedDrawerFormContent
        dockButtons={
          <>
            {fromTypeChoice && (
              <Button
                variant="outline"
                onClick={() => {
                  close();
                  openAddTypeChoiceDrawer();
                }}
              >
                {t('AddNewDrawer.back')}
              </Button>
            )}
            <Button variant="outline" onClick={form.reset}>
              {t('AddNewDrawer.reset')}
            </Button>
            <Button onClick={handleSubmit} loading={isSubmitting}>
              {t('AddNewDrawer.save')}
            </Button>
          </>
        }
      >
        <>
          {canHavePhoto && !uploadImageFile && (
            <Box mb="md">
              <PhotoBannerAlert />
            </Box>
          )}
          {(newItemColumns || []).map(column => (
            <AddNewDrawerFieldRow
              key={`${column?.dataIndex}-newRecord}`}
              column={column}
              renderField={renderField}
            />
          ))}
        </>
      </DockedDrawerFormContent>
    </Drawer>
  );
}

AddNewDrawer.propTypes = {
  itemType: PropTypes.string.isRequired,
  parentRecord: PropTypes.shape({
    uuid: PropTypes.string,
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    merchantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    itemType: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  opened: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  openAddTypeChoiceDrawer: PropTypes.func.isRequired,
  fromTypeChoice: PropTypes.bool,
};
